import React, { useContext, useRef, useState } from 'react'

import {
  Button,
  Modal,
  ModalContext,
  ModalInjectedProps,
} from '@sketch/components'

// Modules
import useUserProfile from 'modules/user/useUserProfile'
import MfaVerificationCodeModal from 'modules/user/modals/MfaAuthenticationModal'
import UserChip from 'modules/sso/components/UserChip'
import SignInWithPasswordForm from 'modules/sso/modals/SignInWithPasswordModal/SignInWithPasswordForm'

// Styles
import { UserChipContainer, Label } from './SignInWithCredentialsModal.styles'

export type SignInWithCredentialsProps = {
  email: string
  name: string
  onSuccess: () => void
  avatarSrc?: string
}

type SignInWithCredentialsModalProps = SignInWithCredentialsProps &
  ModalInjectedProps

const SignInWithCredentialsModal: React.FC<SignInWithCredentialsModalProps> = props => {
  const { hideModal, name, email, avatarSrc, onSuccess } = props

  const [isLoading, setIsLoading] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const { showModal } = useContext(ModalContext)
  const { data } = useUserProfile()
  const isMfaEnabled = data?.me.mfaState === 'ENABLED'

  return (
    <Modal title={'Create a Token'} onCancel={hideModal}>
      <Modal.Body>
        <span>Enter your password to create personal access tokens.</span>
        <UserChipContainer>
          <Label>Sketch Account</Label>
          <UserChip name={name} email={email} avatarSrc={avatarSrc} />
        </UserChipContainer>
        <SignInWithPasswordForm
          submitButtonRef={submitButtonRef}
          email={email}
          onSubmit={() => setIsLoading(true)}
          onCompleted={data => {
            hideModal()

            if (isMfaEnabled && data.signIn.__typename === 'MFACredentials') {
              showModal(MfaVerificationCodeModal, {
                mfaToken: data.signIn.mfaToken,
                onSuccess: onSuccess,
              })
            } else {
              onSuccess()
            }
          }}
          onError={() => setIsLoading(false)}
          onDisableSubmit={disabled => setDisableSubmit(disabled)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={isLoading}
          onClick={() => hideModal()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isLoading || disableSubmit}
          loading={isLoading}
          onClick={() => submitButtonRef.current?.click()}
        >
          Sign In
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SignInWithCredentialsModal
